import { Fragment, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { Placeholder } from 'react-bootstrap';
import PropTypes from 'prop-types';
import {
  COMMON_CURRENCY_PRECISION,
  CRYPTO_CURRENCY_PRECISION,
  TRANSACTION_TYPES as TRANSACTION_TYPE
} from 'components/constants';
import i18nContext from 'components/i18n-context';
import { IS_TRANSFER_TYPE_AVAILABLE } from 'config/config';
import {
  formatDate,
  formatMoney,
  getPaymentMethodClass,
  getTransactionDirectionClass,
  getTransactionDirectionIcon,
  getTransactionStatusClass,
  isExchangeTransaction,
  isCryptoProvider
} from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import './TransactionsTableRow.scss';

const TransactionsTableRow = ({ isLoading, transactionData }) => {
  const i18n = useContext(i18nContext);
  const transactionAmount =
    transactionData.transactionType === TRANSACTION_TYPE.OUTGOING
      ? transactionData?.targetAmount
      : transactionData.sourceAmount;

  const getTransactionInfoBlock = (transactionBlockInfo, isTransactionId = false) => {
    if (isLoading) {
      return (
        <Placeholder as={'p'} animation={'glow'}>
          <Placeholder className={'transactions-loader-placeholder'} xs={12} bg={'secondary'} />
        </Placeholder>
      );
    } else if (isTransactionId) {
      return (
        <NavLink className={'transaction-id-text'} to={`${ROUTE_PATHS.TRANSACTIONS}/${transactionBlockInfo}`}>
          {transactionBlockInfo}
        </NavLink>
      );
    } else if (Array.isArray(transactionBlockInfo)) {
      return (
        <p>
          {transactionBlockInfo.map((item, index) => {
            if (item) {
              return (
                <Fragment key={index}>
                  {item}
                  {index < transactionBlockInfo?.length - 1 && <br />}
                </Fragment>
              );
            }
          })}
        </p>
      );
    } else {
      return <p>{transactionBlockInfo}</p>;
    }
  };

  return (
    <tr>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.transactionNumber, true)}</td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(formatDate(transactionData.createdAt))}</td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(formatDate(transactionData.updatedAt))}</td>
      {IS_TRANSFER_TYPE_AVAILABLE && (
        <td className={'transactions-table-cell'}>
          {getTransactionInfoBlock(i18n.getMessage(`paymentProvider.${transactionData.transferProvider}`))}
        </td>
      )}
      <td className={'transactions-table-cell'}>
        {isLoading ? (
          getTransactionInfoBlock(null)
        ) : (
          <p className={isExchangeTransaction(transactionData.transferType) ? 'transaction-exchange-highlight' : ''}>
            {i18n.getMessage(`transferType.${transactionData.transferType}`)}
          </p>
        )}
      </td>
      <td className={'transactions-table-cell'}>
        {isLoading ? (
          getTransactionInfoBlock(null)
        ) : (
          <p>
            <span className={'transaction-direction-icon'}>
              {getTransactionDirectionIcon(transactionData.transactionType)}{' '}
            </span>
            {i18n.getMessage(`transactionType.${transactionData.transactionType}`)}
          </p>
        )}
      </td>
      <td className={'transactions-table-cell'}>
        {isLoading ? (
          getTransactionInfoBlock(null)
        ) : (
          <p className={getPaymentMethodClass(transactionData.paymentMethod)}>
            {i18n.getMessage(`paymentMethod.${transactionData.paymentMethod}`)}
          </p>
        )}
      </td>
      <td className={'transactions-table-cell'}>
        {isLoading ? (
          getTransactionInfoBlock(null)
        ) : (
          <p className={getTransactionStatusClass(transactionData.status)}>
            {i18n.getMessage(`transactionStatus.${transactionData.status}`)}
          </p>
        )}
      </td>
      <td className={'transactions-table-cell'}>
        {getTransactionInfoBlock(
          formatMoney(
            transactionAmount,
            isCryptoProvider(transactionData.transferProvider) ? CRYPTO_CURRENCY_PRECISION : COMMON_CURRENCY_PRECISION
          )
        )}
      </td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.currency)}</td>
      <td className={'transactions-table-cell'}>
        {getTransactionInfoBlock(
          formatMoney(
            transactionData.commission,
            isCryptoProvider(transactionData.transferProvider) ? CRYPTO_CURRENCY_PRECISION : COMMON_CURRENCY_PRECISION
          )
        )}
      </td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.recipient?.name)}</td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.recipient?.bank)}</td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.recipient?.bankCountry)}</td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.sender?.name)}</td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.sender?.bank)}</td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.sender?.bankCountry)}</td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.comment)}</td>
      <td className={'transactions-table-cell'}>
        {getTransactionInfoBlock(
          transactionData?.hasDocuments
            ? i18n.getMessage('transactions.table.rowBlock.documents.yes')
            : i18n.getMessage('transactions.table.rowBlock.documents.no')
        )}
      </td>
      <td className={'transactions-table-cell'}>{getTransactionInfoBlock(transactionData.externalReference)}</td>
    </tr>
  );
};

TransactionsTableRow.propTypes = {
  isLoading: PropTypes.bool,
  transactionData: PropTypes.object
};

export default TransactionsTableRow;
